import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { UploadCloud, CheckCircle2, AlertCircle, FileText, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 50 * 1024 * 1024;

/**
 * Reusable artwork upload widget.
 * - Drag & drop or click to select
 * - PDF only, max 50MB (client-side check + server enforcement)
 * - Uploads to POST /api/checkout/orders/{order_id}/artwork
 * - Calls onUploaded() with the response so parent can refresh UI
 */
export default function ArtworkUpload({ orderId, currentFile, onUploaded }) {
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const upload = useCallback(async (file) => {
    setError(null);
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError(`File is ${(file.size / 1024 / 1024).toFixed(1)} MB — please keep it under 50 MB.`);
      return;
    }
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      setError("Only PDF files are accepted.");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/checkout/orders/${orderId}/artwork`, form, {
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Artwork uploaded", { description: `${data.filename} (${(data.size / 1024 / 1024).toFixed(2)} MB)` });
      onUploaded?.(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.error || err.message || "Upload failed");
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [orderId, onUploaded]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  }, [upload]);

  return (
    <div data-testid="artwork-upload">
      {currentFile && (
        <div className="mb-4 flex items-center gap-3 border border-gold/30 bg-gold/5 p-4 rounded-sm text-sm">
          <CheckCircle2 size={16} className="text-gold shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-white truncate">{currentFile.filename}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 mt-0.5">
              {((currentFile.size || 0) / 1024 / 1024).toFixed(2)} MB · uploaded
            </div>
          </div>
          <button
            onClick={() => inputRef.current?.click()}
            data-testid="replace-artwork-btn"
            className="text-[10px] uppercase tracking-[0.2em] text-gold hover:underline flex items-center gap-1"
          ><RefreshCw size={11} /> Replace</button>
        </div>
      )}

      <label
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`block cursor-pointer border-2 border-dashed rounded-sm p-10 text-center transition-colors ${
          dragging ? "border-gold bg-gold/10" : uploading ? "border-gold/50 bg-ink-900" : "border-white/15 hover:border-gold/40"
        }`}
        data-testid="artwork-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid="artwork-file-input"
        />
        {uploading ? (
          <div>
            <Loader2 size={28} className="mx-auto text-gold animate-spin mb-3" />
            <div className="text-sm text-white">Uploading… {progress}%</div>
            <div className="mt-3 h-1 bg-ink-800 rounded overflow-hidden max-w-xs mx-auto">
              <div className="h-full bg-gold transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        ) : (
          <div>
            <UploadCloud size={28} className="mx-auto text-gold mb-3" />
            <div className="font-display text-lg text-white">Drop your artwork here</div>
            <p className="text-white/55 text-xs mt-2">or click to browse · PDF only · up to 50 MB</p>
            <div className="mt-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-gold border border-gold px-4 py-2">
              <FileText size={11} /> Choose PDF file
            </div>
          </div>
        )}
      </label>

      {error && (
        <div className="mt-4 flex items-start gap-3 border border-red-500/30 bg-red-500/10 p-4 rounded-sm text-sm">
          <AlertCircle size={16} className="text-red-400 shrink-0 mt-0.5" />
          <div className="text-red-200">{error}</div>
        </div>
      )}

      <p className="mt-4 text-[10px] uppercase tracking-[0.22em] text-white/40 leading-relaxed">
        Reminder · Print-ready PDF · CMYK 300 DPI · 1/16″ bleed on all sides · fonts outlined
      </p>
    </div>
  );
}
